import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Tooltip } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { Rating } from '@material-ui/lab';
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import Alert from "@material-ui/lab/Alert";

export interface QualitativeMetricsProps {
    specQualitativeResultsUrl: string;
    specFile?: string;
    lintingStatus?: string;
}

const QualitativeMetrics: React.FC<QualitativeMetricsProps> = ({ specQualitativeResultsUrl, specFile, lintingStatus }) => {
    const [qualitativeMetrics, setQualitativeMetrics] = useState<any>(null);
    const identityEnvironment = useSelector((state: any) => state?.common?.identityEnvironment ?? "trimble-pre-prod");
    const [qualitativeLoading, setQualitativeLoading] = useState(true);

    useEffect(() => {

        const fetchQualitativeMetrics = async () => {
            try {
                if(specQualitativeResultsUrl !== "") {
                    const response = await fetch(`${specQualitativeResultsUrl}`);
                    if (!response.ok) throw new Error("Failed to fetch qualitative metrics");
                    const jsonData = await response.json();
                    setQualitativeMetrics(jsonData);
                }
            } catch (err) {
                setQualitativeMetrics(null); 
            } finally {
                setQualitativeLoading(false);
            }
        };

        fetchQualitativeMetrics();
    }, [identityEnvironment, specQualitativeResultsUrl]);


    const calculateOverallScore = (metrics: any) => {
        const scores = Object.entries(metrics)
            .filter(([key, value]) => typeof value === 'number') 
            .map(([_, value]) => value as number);
        const total = scores.reduce((acc, score) => acc + score, 0);
        return scores.length > 0 ? total / scores.length : 0; 
    };

    const numericMetricEntries = qualitativeMetrics
        ? Object.entries(qualitativeMetrics).filter(([key]) => typeof qualitativeMetrics[key] === 'number')
        : [];

    const formatMetricName = (key: string) =>
        key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase());

    const criteriaDescriptionMap: Record<string, string> = {
    "Verb Alignment": "Does the verb (HTTP method) in the description correctly match the API's functionality? Does it describe the action appropriately (e.g., GET, POST, DELETE, etc.)?",
    "Path Accuracy": "Does the documentation accurately describe the endpoint path, including path parameters (e.g., /users/{id})? Does it clarify any dynamic path elements (like {id})?",
    "Parameter Alignment": "Are all parameters (query, body, header, or path) correctly described, and do they match the API specification? Are required parameters clearly mentioned?",
    "Clarity And Consistency": "Does the description clearly convey the purpose of the endpoint? Is the information consistent with other parts of the documentation, and are there no contradictions?",
    "Tone And Understandability": "Does the documentation maintain a neutral, clear, and concise tone? Is the text easy to understand for developers, avoiding overly technical language where unnecessary?",
    "Readability Score": "Provide a score based on readability metrics (such as Flesch-Kincaid Grade Level and Flesch Reading Ease). Is the text simple and easy to understand for most developers and explained properly?",
    "Security And Authentication": "Does the documentation clearly explain the security mechanisms in place (e.g., OAuth, API keys)? Are the required authentication methods clearly described?",
    "Examples And Use Cases": "Does the documentation provide practical examples and use cases? Are there sample requests and responses that help developers understand how to use the API?",
    "Versioning And Deprecation": "Does the documentation mention the API version? Are there any notes on deprecated endpoints or upcoming changes?",
    "Error Messages And Troubleshooting": "Are error messages detailed and helpful? Does the documentation provide guidance on how to troubleshoot common issues?",
    "Performance And Rate Limiting": "Does the documentation mention any performance considerations or rate limits? Are there guidelines on how to handle rate limiting?",
    "Internationalization And Localization": "Does the documentation mention support for different languages or locales? Are there any considerations for international users?"
};


    return (
        <Box>
            <Box>
                {qualitativeLoading ? (
                    <CircularProgress />
                ) : qualitativeMetrics ? (
                    <Box>
                        <Box mb={3} textAlign="center">
                            <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '8px', color: '#252a2e' }}>
                                API Standard Conformance
                            </Typography>
                            <Rating
                                name="overall-score"
                                value={calculateOverallScore(qualitativeMetrics) / 2} 
                                precision={0.1}
                                readOnly
                                size="large"
                                style={{ color: '#0063a3' }} 
                            />
                        </Box>
                        <Alert
                            severity="info"
                            style={{
                                marginBottom: '16px',
                                border: '1px solid #0063a3',
                                color: '#0063a3',
                                backgroundColor: '#dcedf9'
                            }}
                        >
                            This grade is based on linting the OpenAPI Definition provided against the Trimble API Standard. To provide feedback on this, please actively engage in the{' '}
                            <a href="https://github.com/trimble-oss/api-standards/discussions" target="_blank" rel="noopener noreferrer" style={{ color: '#0b4f71', textDecoration: 'underline' }}>discussion</a>{' '}
                            on GitHub (available to users in the{' '}
                            <a href="https://github.com/trimble-oss" target="_blank" rel="noopener noreferrer" style={{ color: '#0b4f71', textDecoration: 'underline' }}>trimble-oss</a>{' '}
                            organization).
                        </Alert>
                        {/* Metrics Table */}
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '16px' }}>
                            <thead>
                                <tr>
                                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f4f4f4' }}>Metric</th>
                                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f4f4f4' }}>Score</th>
                                </tr>
                            </thead>
                            <tbody>
                                {numericMetricEntries
                                    .map(([key, value], index) => {
                                        const formattedKey = formatMetricName(key);
                                        return (
                                        <tr key={index}>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                                                <div style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                }}>
                                                {formattedKey}
 
                                                    {criteriaDescriptionMap[formattedKey] && <Tooltip
                                                        title={
                                                            <span>
                                                            {criteriaDescriptionMap[formattedKey]}
                                                            </span>
                                                        }
                                                        placement="right"
                                                        >
                                                        <InfoOutlinedIcon color={"primary"} style={{"fontSize": "16px", "opacity": "0.8"}} />
                                                        </Tooltip>}
                                                </div>
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                                                {value as string | number}
                                            </td>
                                        </tr>
                                    )})}
                            </tbody>
                        </table>
                    </Box>
                ) : (
                    <Typography variant="body1">{specFile ? lintingStatus !== "IN_PROGRESS" ? "Qualitative metrics are unable to process, please re-upload again" : "Qualitative metrics are processing, please check back later." : "No qualitative metrics available."}</Typography>
                )}
            </Box>
        </Box>
    );
};

export default QualitativeMetrics;
